import React from "react";
import {
  MdAdd,
  MdCategory,
  MdEdit,
} from "react-icons/md";
import "./CategoryListing.css";
import { useCategoryListing } from "../../customHook/CategoryListing/useCategoryListing";
import SearchBar from "../../components/SearchBar/SearchBar";
import Toggle from "../../components/Toggle/Toggle";
import InternalPagination from "../../components/InternalPagination/InternalPagination";
import FilterOptions from "../../components/FilterOptions/FilterOptions";
import CategoryModal from "../../components/CategoryModal/CategoryModal";

const getGenderBadgeClass = (gender) => {
  const g = String(gender || "").trim().toLowerCase();
  if (g === "women" || g === "female" || g === "w") {
    return "gender-badge-women";
  }
  if (g === "men" || g === "male" || g === "m") {
    return "gender-badge-men";
  }
  return "bg-slate-100 text-slate-700 border-slate-200/70";
};

const CategoryListing = () => {
  const {
    categories,
    loading,
    error,
    searchInput,
    onSearchChange,
    filterGroups,
    onFilterChange,
    pagination,
    // Modal
    isModalOpen,
    categoryToEdit,
    openAddModal,
    openEditModal,
    closeModal,
    handleSaveCategory,
    // Row actions
    handleToggleStatus,
  } = useCategoryListing();

  return (
    <div className="app-page category-listing-screen flex-1 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Page Header */}
      <div className="product-selector-header px-4 sm:px-6 pt-3 sm:pt-4 pb-2 sm:pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-4 shrink-0">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-headline-md text-lg sm:text-[20px] text-primary font-bold tracking-tight m-0">
              Categories
            </h1>
            <span className="font-body-md inline-flex items-center gap-1.5 text-primary bg-primary/[0.04] border border-primary/10 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold shadow-[0_1px_2px_rgba(0,0,0,0.02)] whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
              {pagination.total} {pagination.total === 1 ? "Category" : "Categories"}
            </span>
          </div>
          <p className="font-body-md text-[10px] sm:text-xs text-secondary mt-0.5">
            Overview and taxonomy management for garment filter categories and collection categories.
          </p>
        </div>

        {/* Header Actions: Add Category Button (Desktop only) */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={openAddModal}
            className="app-button-secondary flex items-center justify-center gap-1.5 px-3 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
            title="Add Category"
          >
            <MdAdd size={16} />
            <span>Add Category</span>
          </button>
        </div>
      </div>

      {/* Primary Controls Bar */}
      <div className="product-controls-wrapper px-4 sm:px-6 pb-2.5 pt-1 shrink-0">
        <div className="app-control-bar product-controls-shell flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4 p-2.5 sm:p-3">
          {/* Search Bar, Filter & Mobile Action */}
          <div className="product-search-actions flex items-center gap-1.5 sm:gap-2 relative min-w-0 w-full sm:w-auto">
            <SearchBar
              value={searchInput}
              onChange={onSearchChange}
              placeholder="Search category name, type...."
              className="flex-1 min-w-0 w-full sm:w-64"
            />
            {/* Filter Options (API-driven) */}
            <FilterOptions
              filters={filterGroups}
              onFilterChange={onFilterChange}
            />
            <button
              type="button"
              onClick={openAddModal}
              className="sm:hidden app-button-secondary flex items-center justify-center gap-1 px-2.5 h-8 rounded-md text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer whitespace-nowrap shrink-0 transition-all"
              title="Add Category"
            >
              <MdAdd size={16} />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="px-4 sm:px-6 pb-3 sm:pb-4 flex-1 min-h-0 flex flex-col">
        {loading && categories.length === 0 ? (
          <div className="app-panel app-empty-state flex-1 flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-slate-200/80">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-semibold text-secondary">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="app-panel app-empty-state flex-1 flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-slate-200/80">
            <MdCategory className="text-[36px] text-slate-300 mb-1" />
            <p className="text-xs font-semibold text-secondary">
              {searchInput
                ? `No categories found matching "${searchInput}"`
                : "No categories found"}
            </p>
          </div>
        ) : (
          <div className="app-panel flex-1 min-h-0 flex flex-col overflow-hidden border border-slate-200/80 rounded-xl bg-white shadow-sm">
            {/* Mobile Card List (Visible only on mobile screens < 640px) */}
            <div className="block sm:hidden flex-1 min-h-0 overflow-y-auto divide-y divide-slate-100 p-3 space-y-2.5">
              {categories.map((row, index) => {
                const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                const formattedNumber = String(rowNumber).padStart(2, "0");

                return (
                  <div
                    key={row.id}
                    className="bg-white border border-slate-200/90 rounded-lg p-3 shadow-2xs flex flex-col gap-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-bold text-slate-400 text-xs shrink-0">#{formattedNumber}</span>
                        <span className="font-bold text-slate-900 text-sm truncate">{row.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Toggle
                          checked={row.isActive}
                          onChange={() => handleToggleStatus(row.id)}
                        />
                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors cursor-pointer"
                          title="Edit Category"
                        >
                          <MdEdit size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
                      <span
                        className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded border ${getGenderBadgeClass(
                          row.gender
                        )}`}
                      >
                        {row.gender}
                      </span>
                      <span className="text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200/60">
                        {row.mode}
                      </span>
                      {row.type === "Collection" ? (
                        <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/70">
                          Collection
                        </span>
                      ) : (
                        <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200/70">
                          Filter
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View (Untouched for screens >= 640px) */}
            <div className="hidden sm:block flex-1 min-h-0 overflow-y-auto overflow-x-auto w-full">
              <table className="w-full border-collapse text-left text-xs text-primary table-auto min-w-[760px]">
                <thead className="sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 shadow-[0_1px_0_rgba(226,232,240,0.9)]">
                  <tr className="border-b border-slate-200/80 text-[10px] text-secondary font-bold uppercase tracking-wider">
                    <th className="px-4 py-3.5 text-center w-16">#</th>
                    <th className="px-4 py-3.5">Category Name</th>
                    <th className="px-4 py-3.5 text-center">Gender</th>
                    <th className="px-4 py-3.5 text-center">Mode</th>
                    <th className="px-4 py-3.5 text-center">Type</th>
                    <th className="px-4 py-3.5 text-center">Status</th>
                    <th className="px-4 py-3.5 text-center w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {categories.map((row, index) => {
                    const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    const formattedNumber = String(rowNumber).padStart(2, "0");

                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50/60 transition-colors"
                      >
                        {/* Row Index */}
                        <td className="px-4 py-3.5 text-center font-bold text-slate-400 text-xs align-middle">
                          {formattedNumber}
                        </td>

                        {/* Category Name */}
                        <td className="px-4 py-3.5 font-bold text-slate-900 text-xs align-middle">
                          {row.name}
                        </td>

                        {/* Gender */}
                        <td className="px-4 py-3.5 text-center align-middle">
                          <span
                            className={`inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-md border ${getGenderBadgeClass(
                              row.gender
                            )}`}
                          >
                            {row.gender}
                          </span>
                        </td>

                        {/* Mode */}
                        <td className="px-4 py-3.5 text-center align-middle">
                          <span className="text-xs text-slate-600 font-medium">
                            {row.mode}
                          </span>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-3.5 text-center align-middle">
                          {row.type === "Collection" ? (
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/70">
                              Collection
                            </span>
                          ) : (
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200/70">
                              Filter
                            </span>
                          )}
                        </td>

                        {/* Status Toggle */}
                        <td className="px-4 py-3.5 text-center align-middle">
                          <div className="flex justify-center">
                            <Toggle
                              checked={row.isActive}
                              onChange={() => handleToggleStatus(row.id)}
                            />
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5 text-center align-middle">
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => openEditModal(row)}
                              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors cursor-pointer"
                              title="Edit Category"
                            >
                              <MdEdit size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Always Visible Fixed Table Footer with Pagination */}
            {pagination && (
              <div className="shrink-0 border-t border-slate-200/80 bg-white/95 backdrop-blur-sm px-3 py-1.5 z-10 overflow-x-auto">
                <InternalPagination
                  page={pagination.page}
                  limit={pagination.limit}
                  total={pagination.total}
                  totalPages={pagination.totalPages}
                  onPageChange={pagination.onPageChange}
                  limitOptions={pagination.limitOptions}
                  onLimitChange={pagination.onLimitChange}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Category Modal (Add / Edit) */}
      <CategoryModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveCategory}
        category={categoryToEdit}
        existingCategories={categories}
      />
    </div>
  );
};

export default CategoryListing;
